<template>
	<div class="charts">
		<div
			v-if="chartData.sectionScores.length < 2"
			class="w-100 d-flex justify-content-center m-3 mb-5"
		>
			<img
				src="../../../assets/overiew-report-ill.svg"
				alt="research-overview"
			/>
		</div>
		<div class="overview-section p-3 pb-5">
			<span class="overview-marks-title"
				>{{ chartData.candidateScore }}/{{ chartData.maxScore }}</span
			>
			<span class="overview-total-title">Total</span>
			<div class="progress-bar">
				<div
					class="progress-bar-fill"
					:style="{
						width: (chartData.candidateScore / chartData.maxScore) * 100 + '%',
					}"
				></div>
			</div>
		</div>
		<div
			v-for="(section, index) in chartData.sectionScores"
			:key="index"
			class="section-card"
		>
			<div class="section-progress-bar w-60">
				<span
					class="section-total-title"
					v-b-popover.hover.top="section.name"
					>{{ section.name | topicNameFilter }}</span
				>
				<div class="progress-bar-section">
					<div
						class="bar-outline"
						:style="`border: solid 0.4px ${getColor(section)}`"
					>
						<div
							class="bar-fill"
							:style="
								`background: ${getColor(section)}; width: ${getWidthPercent(
									section
								)}%`
							"
						></div>
					</div>
				</div>
			</div>
			<div :id="`section-progress-circle-${index}`" class="w-30"></div>
		</div>
	</div>
</template>

<script>
import { select } from "d3-selection";
import { scaleLinear } from "d3-scale";
import { arc } from "d3-shape";
import { getCandidateScoreDetails } from "../../../utils/apiFunctions";

export default {
	name: "OverviewCharts",
	data() {
		return {
			testId: this.$route.params.testID,
			candidateId: this.$route.params.candidateID,
			chartData: {
				maxScore: 50,
				candidateScore: 35,
				sectionScores: [
					{ name: "Psychometry", totalMarks: 15, markScored: 7 },
					{ name: "Quantitative", totalMarks: 15, markScored: 2 },
					{ name: "Technical", totalMarks: 20, markScored: 16 },
					{ name: "Technicality", totalMarks: 30, markScored: 15 },
				],
			},
			//          Good     Average      Bad
			colors: ["#77DD76", "#FDDF5C", "#ff3838"],
		};
	},

	async mounted() {
		try {
			const response = await this.getData();
			this.chartData = response.data;
		} catch (e) {
			console.error("Error getting data", e);
		}
		this.chartData.sectionScores.forEach((section, index) => {
			this.createProgressGraph(
				`#section-progress-circle-${index}`,
				section.totalMarks,
				section.markScored,
				section.name
			);
		});
	},

	methods: {
		async getData() {
			const { data } = await getCandidateScoreDetails(
				this.testId,
				this.candidateId
			);
			if (data.type !== "Success") {
				throw new Error("Request Failed");
			} else {
				return data;
			}
		},

		// Return the index of the colors array
		// 0 -> good, 1 -> average, 2 -> bad
		getColor({ totalMarks, markScored }) {
			const quater = totalMarks / 4;
			if (markScored < quater) {
				return this.colors[2];
			} else if (markScored < 2 * quater) {
				return this.colors[1];
			} else {
				return this.colors[0];
			}
		},

		getWidthPercent({ totalMarks, markScored }) {
			return (markScored / totalMarks) * 100;
		},

		/**
		 * Creates a progress chart
		 * @param {string} parentId - The HTML id attribute of the parent
		 * @param {number} totalMarks - Total marks for the subject
		 * @param {number} markScored - The marks scored out of total
		 * @param {string} title - Title of the chart
		 */
		// createProgressGraph(parentId, totalMarks, markScored, title) {
		createProgressGraph(parentId, totalMarks, markScored) {
			const fillColor = this.getColor({ totalMarks, markScored });
			const marksData = [
				{ value: totalMarks, color: "lightgrey", type: "bg" },
				{ value: markScored, color: fillColor, type: "data" },
			];
			const size = 100;
			const svg = select(parentId)
				.append("svg")
				.attr("viewBox", `-40 -40 ${size * 2} ${size * 2}`);
			// Defining the innershadow
			svg.append("defs")
				.html(`<filter id="shadowFilter" x="-${size}" y="-${size}" width="${size *
				2}" height="${size *
				2}" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">\
                  <feFlood flood-opacity="0" result="BackgroundImageFix"/>\
                  <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>\
                  <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>\
                  <feOffset/>\
                  <feGaussianBlur stdDeviation="2"/>\
                  <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>\
                  <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>\
                  <feBlend mode="normal" in2="shape" result="effect1_innerShadow_2938_196"/>\
                </filter>\
        `);
			const chart = svg
				.append("g")
				.attr("transform", `translate(${size - 40}, ${size - 40})`);

			const angleScale = scaleLinear()
				.domain([0, totalMarks])
				.range([0, -2 * Math.PI]);

			const arcGenerator = arc()
				.innerRadius(size - 40)
				.outerRadius(size - 20)
				.cornerRadius(20)
				.startAngle(angleScale(0))
				.endAngle((data) => angleScale(data.value));

			chart
				.selectAll("path")
				.data(marksData)
				.enter()
				.append("path")
				.attr("d", arcGenerator)
				.attr("filter", "url(#shadowFilter)")
				.attr("stroke", (data) => (data.type === "bg" ? data.color : "none"))
				.attr("fill", (data) => (data.type === "bg" ? "#FFFFFF" : data.color));
			chart
				.append("text")
				.text(`${markScored}`)
				.attr("fill", fillColor)
				.attr("font-size", "40px")
				.attr("font-weight", "800")
				.attr("text-anchor", "middle")
				.attr("dominant-baseline", "central")
				.attr("transform", `translate(0,0)`);
		},
	},
};
</script>

<style scoped>
.charts {
	width: 100%;
	height: 55vh;
	overflow: scroll;
	-ms-overflow-style: none;
	scrollbar-width: none;
}

.charts ::-webkit-scrollbar {
	display: none;
}

.progress-bar {
	width: 100%;
	height: 15px;
	background: #7459d933;
	border-radius: 20px;
}

.progress-bar-fill {
	background: #7459d9;
	height: 100%;
	border-radius: 20px;
}

.overview-marks-title {
	font-family: "Poppins", sans-serif;
	font-size: var(--size-30);
	font-weight: 700;
	color: #72738e;
	margin-right: 10px;
}

.overview-total-title {
	font-family: "Poppins", sans-serif;
	font-size: var(--size-14);
	margin-bottom: 0.7rem;
	color: #72738e94;
}

.section-total-title {
	font-family: "Poppins", sans-serif;
	font-size: var(--size-16);
	display: block;
	margin-bottom: 0.7rem;
}

svg {
	overflow: visible;
}

#section-marks {
	display: flex;
	width: 60%;
	justify-content: center;
	align-items: center;
}

.overview-progress-bar {
	margin-left: 1rem;
}

.w-30 {
	width: 30%;
}

.w-60 {
	width: 60%;
}

.section-card {
	margin: 1rem;
	padding: 0.5rem 2rem;
	box-shadow: 0px 4px 20px rgba(196, 196, 196, 0.5);
	border-radius: 5px;
	display: flex;
	align-items: center;
	justify-content: space-between;
}

.overview-section {
	position: sticky;
	top: 0;
	left: 0;
	background: #ffffff;
}

.progress-bar-section > .bar-outline {
	height: 5px;
	background: #ffffff;
	border-radius: 3px;
	width: 100%;
}

.progress-bar-section > .bar-outline .bar-fill {
	height: 100%;
	border-radius: 3px;
}

@media only screen and (max-width: 768px) {
	.charts {
		flex-direction: column;
	}
	#section-marks {
		flex-direction: column;
	}
}
</style>
